// FO-094 (batch-23) — Types, DTO's en Zod-schemas voor het contractbeheer:
// arbeidscontract-documenten met een status-workflow (concept -> ter
// ondertekening -> ondertekend -> actief -> beeindigd, plus verlopen/
// ingetrokken) en optionele digitale ondertekening door de medewerker.
//
// Het EmploymentContract-document is de ondertekening-/dossier-laag BOVENOP de
// arbeidsvoorwaarden-/verloning-waarheid (`EmployeeContractMutation`). Type,
// duur, uren, start en eind worden uit de contract-state VOORGEVULD (principe
// 3); de mutatie-tijdlijn blijft leidend voor verlof/payroll en wordt NIET
// gedupliceerd (principe 4).
//
// DTO's gebruiken ISO-datum-strings; conversie van/naar `Date` gebeurt in de
// API-laag zodat de types client-vriendelijk (serializable over HTTP) blijven.

import { z } from 'zod'
import {
  OVEREENKOMST_FASES,
  OVEREENKOMST_REDENEN,
  type OvereenkomstFase,
  type OvereenkomstReden,
} from './overeenkomsten'
import { validatePlaceholderValues, validateVerlengingRequired } from './document-placeholders'

// ── Enums (mirror Prisma EmploymentContractType / EmploymentContractStatus) ──

export const EMPLOYMENT_CONTRACT_TYPES = [
  'ARBEIDSOVEREENKOMST',
  'VERLENGING',
  'VERKLARING',
  'GEHEIMHOUDING',
] as const
export type EmploymentContractType = (typeof EMPLOYMENT_CONTRACT_TYPES)[number]

export const EMPLOYMENT_CONTRACT_STATUSES = [
  'CONCEPT',
  'TER_ONDERTEKENING',
  'ONDERTEKEND',
  'ACTIEF',
  'BEEINDIGD',
  'VERLOPEN',
  'INGETROKKEN',
] as const
export type EmploymentContractStatus = (typeof EMPLOYMENT_CONTRACT_STATUSES)[number]

// Snapshot van de contractduur uit de contract-state (Prisma `ContractType`).
// Bepaalt mede of een einddatum verplicht is en wordt op het contractdocument
// getoond. NIET de bron van waarheid (dat blijft `EmployeeContractMutation`) —
// puur voorvulling + labeling + de eindDatum-verplichting.
export const EMPLOYMENT_CONTRACT_DUUR_TYPES = [
  'BEPAALDE_TIJD',
  'ONBEPAALDE_TIJD',
  'OPROEP',
  'STAGE',
  'PAYROLL',
] as const
export type EmploymentContractDuurType = (typeof EMPLOYMENT_CONTRACT_DUUR_TYPES)[number]

// ── Labels (NL voor UI) ──────────────────────────────────────────────────────

export const EMPLOYMENT_CONTRACT_TYPE_LABELS: Record<EmploymentContractType, string> = {
  ARBEIDSOVEREENKOMST: 'Arbeidsovereenkomst',
  VERLENGING:          'Verlenging',
  VERKLARING:          'Verklaring',
  GEHEIMHOUDING:       'Geheimhoudingsverklaring',
}

export const EMPLOYMENT_CONTRACT_STATUS_LABELS: Record<EmploymentContractStatus, string> = {
  CONCEPT:           'Concept',
  TER_ONDERTEKENING: 'Ter ondertekening',
  ONDERTEKEND:       'Ondertekend',
  ACTIEF:            'Actief',
  BEEINDIGD:         'Beëindigd',
  VERLOPEN:          'Verlopen',
  INGETROKKEN:       'Ingetrokken',
}

export const EMPLOYMENT_CONTRACT_DUUR_LABELS: Record<EmploymentContractDuurType, string> = {
  BEPAALDE_TIJD:   'Bepaalde tijd',
  ONBEPAALDE_TIJD: 'Onbepaalde tijd',
  OPROEP:          'Oproep',
  STAGE:           'Stage',
  PAYROLL:         'Payroll',
}

// Herkomst van een contractdocument (spec 41). GEGENEREERD = via de FO-094
// workflow; EXTERN_GEUPLOAD = HR uploadt een reeds-getekend/extern stuk;
// GEMIGREERD = uit de eenmalige migratie van losse EmployeeDocument-uploads.
export const EMPLOYMENT_CONTRACT_BRON_TYPES = [
  'GEGENEREERD',
  'EXTERN_GEUPLOAD',
  'GEMIGREERD',
] as const
export type EmploymentContractBron = (typeof EMPLOYMENT_CONTRACT_BRON_TYPES)[number]

export const EMPLOYMENT_CONTRACT_BRON_LABELS: Record<EmploymentContractBron, string> = {
  GEGENEREERD:     'Gegenereerd',
  EXTERN_GEUPLOAD: 'Extern aangeleverd',
  GEMIGREERD:      'Gemigreerd',
}

/**
 * Een contract is "tijdelijk" (en vereist dus een einddatum) wanneer het een
 * verlenging is, of wanneer de gespiegelde contractduur niet onbepaalde tijd
 * is. Gedeeld door het Zod-schema (server + form) zodat de eindDatum-regel op
 * een plek staat.
 */
export function isTijdelijkContract(
  type: EmploymentContractType,
  contractType: EmploymentContractDuurType | null | undefined,
): boolean {
  if (type === 'VERLENGING') return true
  return contractType != null && contractType !== 'ONBEPAALDE_TIJD'
}

/**
 * FO-143 — leid de contractvorm (bepaalde/onbepaalde tijd) af uit de vrije-tekst
 * `contractperiode` van een recruitment-aanbieding ("7 maanden", "1 jaar",
 * "onbepaalde tijd"). Er is geen gestructureerd vorm-veld op de aanbieding, dus
 * dit is een best-effort voorvulling die HR in de wizard nog kan corrigeren.
 * Alleen "onbepaald(e)" leidt tot ONBEPAALDE_TIJD; al het overige (incl. leeg)
 * valt terug op BEPAALDE_TIJD (het gangbare eerste contract).
 */
export function contractvormUitContractperiode(
  periode: string | null | undefined,
): Extract<EmploymentContractDuurType, 'BEPAALDE_TIJD' | 'ONBEPAALDE_TIJD'> {
  if (periode && /onbepaald/i.test(periode)) return 'ONBEPAALDE_TIJD'
  return 'BEPAALDE_TIJD'
}

// ── DTO's (response shapes) ──────────────────────────────────────────────────

/** Volledige backoffice-DTO (HR/management). */
export interface EmploymentContractDTO {
  id: string
  employeeId: string
  type: EmploymentContractType
  typeLabel: string
  status: EmploymentContractStatus
  statusLabel: string
  /** Herkomst: gegenereerd via workflow, extern geüpload, of gemigreerd (spec 41). */
  bron: EmploymentContractBron
  bronLabel: string
  contractType: EmploymentContractDuurType | null
  contractDuurLabel: string | null
  startDatum: string // ISO-date (yyyy-mm-dd)
  eindDatum: string | null
  ondertekeningVerplicht: boolean
  // Workflow-stempels (wie/wanneer per stap; de audit-tabel houdt de historie).
  vrijgegevenOp: string | null
  ondertekendOp: string | null
  geactiveerdOp: string | null
  beeindigdOp: string | null
  // Koppelingen
  documentId: string | null
  /** Hoogste versienr van het gekoppelde document-PDF (voor de download-URL). */
  documentVersieNr: number | null
  signatureId: string | null
  vervangtId: string | null
  registratieId: string | null
  note: string | null
  // FO-139 (batch-27) — Overeenkomsten-laag (fase commercieel, naast status).
  fase: OvereenkomstFase
  faseLabel: string
  reden: OvereenkomstReden | null
  redenLabel: string | null
  templateId: string | null
  /** Er is een Word-versie (merge-DOCX) bewaard naast de PDF. */
  heeftDocx: boolean
  // Afgeleide UI-helpers (server berekent in Europe/Amsterdam)
  isGetekend: boolean
  isVrijgegeven: boolean
  /** true zodra er een gegenereerd contract-PDF aan hangt. */
  heeftDocument: boolean
  /** Dagen tot de einddatum (negatief = al verlopen); null bij onbepaalde tijd. */
  dagenTotEinde: number | null
  /** Aandachtsmarkering: tijdelijk contract binnen de signalering-drempel. */
  aandachtAflopend: boolean
  /**
   * FO-388 (2026-10) - stand van het verleng-traject zodat de Contracten-tab de
   * ECHTE volgende stap toont i.p.v. altijd "besluit nodig". Null = nog geen
   * besluit vastgelegd.
   */
  verlengBesluit: {
    besluitLabel: string
    /** Wat er nog moet gebeuren; null = traject compleet (besluit + evt. aanzegging). */
    volgendeStap: 'AANZEGBRIEF' | 'AANZEGGING' | null
  } | null
  createdByName: string | null
  createdAt: string
  updatedAt: string
}

/**
 * Slanke portal-DTO voor `GET /me/contracten` — alleen wat de medewerker mag
 * zien van zijn EIGEN, VRIJGEGEVEN contract. Bevat bewust geen interne velden
 * (createdBy, registratie, vervangt, note).
 */
export interface MyEmploymentContractDTO {
  id: string
  type: EmploymentContractType
  typeLabel: string
  status: EmploymentContractStatus
  statusLabel: string
  startDatum: string
  eindDatum: string | null
  documentId: string | null
  /** Hoogste versienr van het gekoppelde document-PDF (voor de download-URL). */
  documentVersieNr: number | null
  /** Ondertekening verplicht én nog niet getekend door de medewerker. */
  moetOndertekenen: boolean
  isGetekend: boolean
  ondertekendOp: string | null
  vrijgegevenOp: string | null
}

// ── FO-143: arbeidsovereenkomst vanuit een aangenomen kandidaat ──────────────

/**
 * Slanke kandidaat-optie voor de bronkeuze "aangenomen kandidaat" in de wizard
 * (MEDEWERKERS-gated lijst). `employeeId` gezet = al geconverteerd naar een
 * medewerker; null = de doorstroom converteert eerst (in de recruitment-context).
 */
export interface WizardKandidaatDTO {
  id: string
  naam: string
  functie: string
  employeeId: string | null
}

/** Voorvulling voor de contractstap, afgeleid uit de aanbieding/het dossier. */
export interface EmploymentContractPrefill {
  type: EmploymentContractType
  contractType: EmploymentContractDuurType
  startDatum: string | null // ISO-date (yyyy-mm-dd)
  eindDatum: string | null
  note: string | null
  reden: OvereenkomstReden | null
}

/**
 * Antwoord van de kandidaat-prefill: de (eventueel al geconverteerde) medewerker
 * + de bron-aanbieding + de voorgevulde contractvelden. Geconverteerd → prefill
 * uit de contract-state (waarheid); niet geconverteerd → uit de aanbieding.
 */
export interface KandidaatContractPrefillDTO {
  kandidaatId: string
  naam: string
  employeeId: string | null
  aanbiedingId: string | null
  prefill: EmploymentContractPrefill
}

// ── Zod-schemas (request bodies) ─────────────────────────────────────────────

// Eén gedeeld create/patch-schema: de API gebruikt het (`safeParse`) én het
// form valideert er client-side mee, zodat het form niets kan sturen dat de API
// weigert. We gebruiken bewust GEEN `.partial()` voor PATCH (Zod-4 behoudt
// `.default()` daarop, repo-gotcha) — bewerken mag alleen in CONCEPT en het
// form stuurt dan het volledige object, dus PATCH hergebruikt dit schema.
const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

export const employmentContractFormSchema = z
  .object({
    type:                   z.enum(EMPLOYMENT_CONTRACT_TYPES),
    contractType:           z.enum(EMPLOYMENT_CONTRACT_DUUR_TYPES).nullable().optional(),
    // ISO-kalenderdatum (jjjj-mm-dd, zoals de DatePicker levert) zodat een
    // malformed string een nette 400 geeft i.p.v. een Invalid Date -> 500.
    startDatum:             z.string().min(1, 'Startdatum is verplicht.').regex(ISO_DATE, 'Geef een geldige startdatum op.'),
    eindDatum:              z.string().regex(ISO_DATE, 'Geef een geldige einddatum op.').nullable().optional(),
    ondertekeningVerplicht: z.boolean().optional(),
    vervangtId:             z.string().nullable().optional(),
    // FO-143 — optionele bron-aanbieding (alleen bij de bron "aangenomen
    // kandidaat"). Nullable/optional, spiegelt vervangtId/templateId.
    aanbiedingId:           z.string().min(1).nullable().optional(),
    note:                   z.string().max(2000).nullable().optional(),
    // FO-139 (batch-27) — Overeenkomsten-laag. `fase` (commercieel) + `reden`;
    // `templateId`/`placeholderValues` sturen de DOCX-template-generatie (Word+PDF).
    fase:                   z.enum(OVEREENKOMST_FASES).optional(),
    reden:                  z.enum(OVEREENKOMST_REDENEN).nullable().optional(),
    templateId:             z.string().min(1).nullable().optional(),
    placeholderValues:      z.record(z.string(), z.string()).nullable().optional(),
  })
  .superRefine((val, ctx) => {
    const eind = val.eindDatum && val.eindDatum.trim() ? val.eindDatum.trim() : null
    if (isTijdelijkContract(val.type, val.contractType ?? null) && !eind) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eindDatum'],
        message: 'Einddatum is verplicht bij een tijdelijk contract.',
      })
    }
    // FO-312 — getypeerde placeholder-validatie (dropdown-waarden ∈ opties,
    // decimal/int/datum-vorm) via de gedeelde registry. Inline per veld, zodat
    // een fout op `placeholderValues.<key>` landt (geen generieke toast).
    if (val.placeholderValues) {
      for (const issue of validatePlaceholderValues(val.placeholderValues)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['placeholderValues', issue.key],
          message: issue.message,
        })
      }
      // FO-312 §5 - bij verlenging mogen de vorig-contract-velden niet leeg zijn.
      for (const issue of validateVerlengingRequired(val.placeholderValues)) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['placeholderValues', issue.key],
          message: issue.message,
        })
      }
    }
    if (eind && val.startDatum) {
      // Vergelijk op kalenderdag (DatePicker levert yyyy-mm-dd).
      const start = new Date(val.startDatum)
      const einde = new Date(eind)
      if (
        !Number.isNaN(start.getTime()) &&
        !Number.isNaN(einde.getTime()) &&
        einde.getTime() < start.getTime()
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['eindDatum'],
          message: 'Einddatum mag niet voor de startdatum liggen.',
        })
      }
    }
  })
export type EmploymentContractFormInput = z.infer<typeof employmentContractFormSchema>

// FO-139 — "nieuwe medewerker"-stap in de arbeidsovereenkomst-wizard: maak een
// medewerker-CONCEPT (zonder account-activatie) zodat er direct een overeenkomst
// aan gehangen kan worden. Alleen de minimale identiteits-/contractvelden.
export const wizardCreateEmployeeSchema = z
  .object({
    firstName: z.string().trim().min(1, 'Voornaam is verplicht.'),
    lastName: z.string().trim().min(1, 'Achternaam is verplicht.'),
    email: z.string().trim().email('Geef een geldig e-mailadres op.').optional().or(z.literal('')),
    functionTitle: z.string().trim().max(160).optional().or(z.literal('')),
    contractType: z.enum(EMPLOYMENT_CONTRACT_DUUR_TYPES),
    startDatum: z.string().min(1, 'Startdatum is verplicht.').regex(ISO_DATE, 'Geef een geldige startdatum op.'),
    eindDatum: z.string().regex(ISO_DATE, 'Geef een geldige einddatum op.').nullable().optional(),
    contractHoursPerWeek: z.number().positive('Uren moeten groter dan 0 zijn.').max(80).optional(),
  })
  .superRefine((val, ctx) => {
    const eind = val.eindDatum && val.eindDatum.trim() ? val.eindDatum.trim() : null
    if (val.contractType !== 'ONBEPAALDE_TIJD' && !eind) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['eindDatum'],
        message: 'Einddatum is verplicht bij een tijdelijk contract.',
      })
    }
  })
export type WizardCreateEmployeeInput = z.infer<typeof wizardCreateEmployeeSchema>

// Labels voor `summarizeValidationIssues` (inline veld-fout-fallback).
export const EMPLOYMENT_CONTRACT_FIELD_LABELS: Record<string, string> = {
  type:        'Type',
  contractType:'Contractduur',
  startDatum:  'Startdatum',
  eindDatum:   'Einddatum',
  note:        'Toelichting',
}
